'use client'

/**
 * Requests from the Presenter and the Data Manager (plans/07 section 3.3).
 *
 * Every call except `GET /api/dr/story` must carry `X-AIO-Persona:
 * supervity-support`, whatever persona the window shows, so this helper sets
 * the header on each request. It also reads the Rule 08 error body
 * (`{"error": {"code", "message"}}`), so a refusal such as "The story only
 * moves forward. Reset, then pick the step." reaches the toast word for word.
 */

import { getSession } from 'next-auth/react'
import { PERSONA_HEADER } from '@/lib/persona'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

export const SUPPORT_PERSONA = 'supervity-support'

const FALLBACK_MESSAGE = 'The request did not go through. Try again.'

/** A refusal or failure with the server's sentence, its HTTP status and its code. */
export class SupportApiError extends Error {
  readonly status: number
  readonly code: string | null

  constructor(message: string, status: number, code: string | null = null) {
    super(message)
    this.name = 'SupportApiError'
    this.status = status
    this.code = code
  }
}

/** A request block exactly as the server hands it on a button (`{method, path, query, body}`). */
export interface RequestBlock {
  method: string
  path: string
  query?: Record<string, string> | null
  body?: Record<string, unknown> | null
}

export interface SupportRequestOptions {
  query?: Record<string, string | number | boolean | null | undefined> | null
  body?: unknown
  form?: FormData
  signal?: AbortSignal
}

function errorMessage(payload: unknown, status: number): { message: string; code: string | null } {
  if (payload && typeof payload === 'object') {
    const record = payload as { error?: { message?: unknown; code?: unknown }; detail?: unknown }
    if (record.error && typeof record.error.message === 'string' && record.error.message) {
      return { message: record.error.message, code: typeof record.error.code === 'string' ? record.error.code : null }
    }
    if (typeof record.detail === 'string' && record.detail) return { message: record.detail, code: null }
    if (Array.isArray(record.detail)) {
      const parts = record.detail
        .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : ''))
        .filter(Boolean)
      if (parts.length) return { message: parts.join('; '), code: null }
    }
  }
  if (status === 501) return { message: 'This part is not built yet.', code: 'NOT_BUILT' }
  return { message: FALLBACK_MESSAGE, code: null }
}

function buildUrl(path: string, query?: SupportRequestOptions['query']): string {
  const params = new URLSearchParams()
  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') params.set(key, String(value))
  })
  const qs = params.toString()
  return `${API_URL}${BASE_PATH}${path}${qs ? `?${qs}` : ''}`
}

/** One request as Supervity support. Throws `SupportApiError` with the server's sentence on any refusal. */
export async function supportRequest<T>(method: string, path: string, options: SupportRequestOptions = {}): Promise<T> {
  const session = await getSession()
  const headers = new Headers()
  if (session?.accessToken) headers.set('Authorization', `Bearer ${session.accessToken}`)
  headers.set(PERSONA_HEADER, SUPPORT_PERSONA)

  let body: BodyInit | undefined
  if (options.form) {
    body = options.form
  } else if (method !== 'GET' && options.body !== undefined) {
    headers.set('Content-Type', 'application/json')
    body = JSON.stringify(options.body ?? {})
  }

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), { method, headers, body, signal: options.signal })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new SupportApiError('The Disaster Management AI Office could not be reached. Check the connection and try again.', 0)
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => null)
    const { message, code } =
      response.status === 401
        ? { message: 'Your session has expired. Please sign in again.', code: 'UNAUTHORIZED' }
        : errorMessage(payload, response.status)
    throw new SupportApiError(message, response.status, code)
  }
  if (response.status === 204) return null as T
  return (await response.json()) as T
}

/** Posts a server-built request block as-is (trigger and replay buttons). */
export function runRequestBlock<T>(block: RequestBlock): Promise<T> {
  return supportRequest<T>(block.method || 'POST', block.path, { query: block.query ?? null, body: block.body ?? {} })
}

/** The sentence to show for any error thrown by these helpers. */
export function errorText(err: unknown): string {
  if (err instanceof SupportApiError) return err.message
  if (err instanceof Error && err.message) return err.message
  return FALLBACK_MESSAGE
}

/** HTTP status of an error from these helpers, or null. */
export function errorStatus(err: unknown): number | null {
  return err instanceof SupportApiError ? err.status : null
}
